/**
 * The 3Cs / feel contract — now a discriminated union by archetype (design §15).
 * One profile per game (`design/three-cs.json`); each spatial kind parses the variant
 * it consumes and reports a mismatch as a finding rather than crashing.
 */
import { z } from 'zod';

/** Side-scroll 2D (design §17). `locomotion: 'auto'` (the default — Bumble-style runner)
 *  or `'player'` (Mario-like: the player drives horizontal movement, so pacing is freer).
 *  `tether` is OPTIONAL — solo/untethered games skip the group-solvability checks. */
export const AutorunnerThreeCsSchema = z
  .object({
    archetype: z.literal('autorunner-2d'),
    /** Absent = 'auto' (back-compat with MVP1 profiles). */
    locomotion: z.enum(['auto', 'player']).optional(),
    character: z.object({
      autoRunSpeed: z.number().finite().positive(),
      backpedalSpeed: z.number().finite().negative(),
      jumpHeight: z.number().finite().positive(),
      jumpAirTime: z.number().finite().positive(), // full symmetric airtime
      downedDragSpeedPenalty: z.number().finite().min(0).lt(1),
      bodyWidth: z.number().finite().positive(),
    }),
    tether: z
      .object({
        members: z.number().int().min(2),
        restLength: z.number().finite().positive(),
        maxStretch: z.number().finite().positive(),
      })
      .optional(),
    /** Optional per-level pace ramp (design §17). Many autorunners speed up as the
     *  campaign progresses, so a level's SOLVABLE geometry depends on the speed IT
     *  runs at, not the level-1 base. When present, a level's effective auto-run speed
     *  = min(maxSpeed, autoRunSpeed + playIndex·escalationPerLevel), playIndex being its
     *  0-based position in the progression. Absent = a flat autoRunSpeed for every level
     *  (the back-compat default). The validator uses this so later, faster levels are
     *  checked against their real reach instead of failing against the base speed. */
    pace: z
      .object({
        escalationPerLevel: z.number().finite().min(0),
        maxSpeed: z.number().finite().positive(),
      })
      .optional(),
    solver: z
      .object({
        collapseSafetyMargin: z.number().finite().min(0).optional(),
        clearanceMargin: z.number().finite().min(0).optional(),
      })
      .optional(),
  })
  .refine((p) => !p.tether || p.tether.maxStretch >= p.tether.restLength, {
    message: 'tether.maxStretch must be >= tether.restLength',
    path: ['tether', 'maxStretch'],
  });

export type AutorunnerThreeCs = z.infer<typeof AutorunnerThreeCsSchema>;

/** Top-down action-adventure (StarTropics-like). No gravity, no jump, no tether. */
export const TopdownThreeCsSchema = z.object({
  archetype: z.literal('topdown-2d'),
  character: z.object({
    moveSpeed: z.number().finite().positive(), // m/s
    accel: z.number().finite().positive(),
    /** Collision footprint radius (m) — corridors must fit 2r + clearance. */
    bodyRadius: z.number().finite().positive(),
  }),
  camera: z.object({
    /** World units visible (orthographic framing budget). */
    viewW: z.number().finite().positive(),
    viewH: z.number().finite().positive(),
  }),
  solver: z
    .object({
      clearanceMargin: z.number().finite().min(0).optional(),
    })
    .optional(),
});

export type TopdownThreeCs = z.infer<typeof TopdownThreeCsSchema>;

/** Property-driven game (match-3 boards, gem loot tables, grab-bag sources) — levels are
 *  data blobs (`data-table` kind), so there are no kinematics to contract. */
export const DataThreeCsSchema = z.object({
  archetype: z.literal('data-table'),
  /** What the data levels represent (e.g. "starting boards + gem loot tables"). */
  notes: z.string().optional(),
});

export type DataThreeCs = z.infer<typeof DataThreeCsSchema>;

/** The game's feel profile — exactly one variant per game. */
export const ThreeCsProfileSchema = z.discriminatedUnion('archetype', [
  // z.discriminatedUnion rejects ZodEffects members; the tether refine moves to a superRefine-free
  // inner shape check here instead (validated again by the autorunner kind at consume time).
  AutorunnerThreeCsSchema.innerType(),
  TopdownThreeCsSchema,
  DataThreeCsSchema,
]);

export type ThreeCsProfile = z.infer<typeof ThreeCsProfileSchema>;
